import { Button, Col, Collapse, Container, Row, Stack } from 'react-bootstrap';
import { convertIntUnixToFormateDate } from '../../../utils/dateUtils';
import DateFilter from '../../../components/Filters/components/DateFilter';
import SelectFilter from '../../../components/Filters/components/SelectFilter';
import MultipleSelectFilter from '../../../components/Filters/components/MultipleSelectFilter';
import { FilterDto, FilterType } from '../../../types/FilterDto';
import { useTranslation } from 'react-i18next';
import { useTableFilterManager } from '../../../hooks/useTableFilterManager';
import { FilterTableKey } from '../../../redux/filtersSlice';
import { HistoricalTableKey } from '../../../redux/historicalSlice';
import SelectedFilterChip from './SelectedFilterChip';
import { SelectedFilter } from '../../../components/Filters/types';
import { KeyValuePair } from '../../../types/KeyValuePair';
import SentimentIndicator from '../../SentimentIndicatorSelector';
import AppHierarchicalMultiSelectFilter from './AppHierarchicalMultiSelectFilter';

interface FilterProps {
    filters: FilterDto[];
    isFilterVisible: boolean;
    tableKey: FilterTableKey;
    historicalKey: HistoricalTableKey;
    onFiltersChange?: (filters: SelectedFilter[]) => void;
}

export default function FiltersContainer({ filters, isFilterVisible, tableKey, historicalKey, onFiltersChange }: FilterProps) {
    const { t } = useTranslation(['common', 'alert', 'dashboard']);
    const getFilterConfig = (name: string) => filters.find(f => f.name === name);

    const getDisplayValue = (opt: KeyValuePair | undefined, fallback: string) => {
        if (!opt?.value) return fallback;

        if (typeof opt.value === 'object') {
            const v = opt.value as any;

            if (v.description) {
                return v.description;
            }
        }

        return String(opt.value);
    };

    const {
        selectedFilters,
        handleFilterChange,
        handleRemoveFilter,
        handleRemoveRelatedFilter,
        handleDateFilterChange,
        handleRemoveDateFilter,
        handleClearAllFilters
    } = useTableFilterManager({
        tableKey,
        historicalKey,
        hasPagination: true,
        onFiltersChange,
    });

    const hasActiveFilters = selectedFilters.some(f =>
        (f.values && f.values.length > 0) ||
        (f.dateValues?.initialDate || f.dateValues?.finalDate)
    );

    const isSentimentFilter = (name: string) => name === "SentimentAI";

    const renderFilter = (filter: FilterDto) => {
        if (isSentimentFilter(filter.name)) {
            const currentValues = (selectedFilters.find(f => f.name === filter.name)?.values ?? []).map(Number);
            const sentiments = (filter.values ?? []).map((kv, index) => ({
                id: index,
                value: Number(kv.key),
                type: String(kv.value),
                startDate: '',
            })).filter(sentiment => !Number.isNaN(sentiment.value));
            const sentimentValues = sentiments.map(sentiment => sentiment.value);
            return (
                <SentimentIndicator
                    multiSelect
                    label={t(filter.description)}
                    values={currentValues}
                    onChange={(vals) => handleFilterChange(
                        filter.name,
                        vals.map(String),
                        filter.description
                    )}
                    sentiments={sentiments}
                    minValue={sentimentValues.length ? Math.min(...sentimentValues) : undefined}
                    maxValue={sentimentValues.length ? Math.max(...sentimentValues) : undefined}
                />
            );
        }

        switch (filter.type) {
            case FilterType.DATE:
                return (
                    <DateFilter
                        filter={filter}
                        values={selectedFilters?.find(filterValue => filterValue.name === filter.name)?.dateValues || {}}
                        onSelectChange={handleDateFilterChange}
                    />
                );
            case FilterType.SELECT:
                return <SelectFilter
                    filter={filter}
                    values={selectedFilters?.find(filterValue => filterValue.name === filter.name)?.values || []}
                    onSelectChange={handleFilterChange} />;
            case FilterType.MULTIPLE:
                return (
                    <MultipleSelectFilter
                        filter={filter}
                        values={selectedFilters?.find(filterValue => filterValue.name === filter.name)?.values || []}
                        onSelectChange={handleFilterChange}
                    />
                );
            case FilterType.MULTIPLE_TREE:
                return (
                    <AppHierarchicalMultiSelectFilter
                        filter={filter}
                        values={selectedFilters?.find(filterValue => filterValue.name === filter.name)?.values || []}
                        onSelectChange={handleFilterChange}
                    />
                );
            default:
                return null;
        }
    };
    return (
        <div className="filters-wrapper">
            <Collapse in={isFilterVisible} timeout={1000} >
                <div id="collapse-filter-id">
                    <Container fluid className="filtres pt-1 pb-3 px-3">
                        <Row className="w-100 gx-2 gy-1 align-items-end">
                            {(filters ?? []).map((filtre, index) => (
                                <Col xs="auto" key={index}>
                                    {renderFilter(filtre)}
                                </Col>
                            ))}
                        </Row>
                    </Container>
                </div>
            </Collapse>
            <Container fluid className="px-3">
                {!!selectedFilters.length &&
                    <Row className="w-100 d-flex align-items-center">
                        <Container fluid className="px-0">
                            {hasActiveFilters && (
                                <Row className="w-100 d-flex align-items-center">
                                    {
                                        selectedFilters.map((selectedFilter) => {
                                            if (selectedFilter.relatedFilters) {
                                                return (
                                                    <>
                                                        {selectedFilter.relatedFilters.map((relatedFilter) => {
                                                            const relatedValues = relatedFilter.values ?? [];
                                                            if (!relatedValues.length)
                                                                return null;
                                                            return(
                                                                <Col xs="auto" key={`${selectedFilter.name}_${relatedFilter.name}`}>
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <span>{t(relatedFilter.description ?? "")}:</span>
                                                                        <Stack direction="horizontal" gap={2}>
                                                                            {relatedValues.map((relatedValue) => (
                                                                                <SelectedFilterChip
                                                                                    key={relatedValue}
                                                                                    label=""
                                                                                    value={relatedValue}
                                                                                    onRemove={() => handleRemoveRelatedFilter(selectedFilter.name, relatedFilter.name, relatedValue)}
                                                                                />
                                                                            ))}
                                                                        </Stack>
                                                                    </div>
                                                                </Col>
                                                            )
                                                        })}
                                                    </>
                                                );
                                            }

                                            const filterConfig = getFilterConfig(selectedFilter.name);
                                            if (!filterConfig) return null;

                                            // DATE filters
                                            if (filterConfig.type === FilterType.DATE) {
                                                return (
                                                    <Col xs="auto" key={selectedFilter.name}>
                                                            <Stack direction="horizontal" gap={2}>
                                                                {selectedFilter.dateValues?.initialDate && (
                                                                    <SelectedFilterChip
                                                                    label={`${filterConfig.description} ${t("dashboard:filters.Inicial")}:`}
                                                                        value={convertIntUnixToFormateDate(selectedFilter.dateValues.initialDate)}
                                                                        onRemove={() => handleRemoveDateFilter(selectedFilter.name, true)}
                                                                    />
                                                                )}
                                                                {selectedFilter.dateValues?.finalDate && (
                                                                    <SelectedFilterChip
                                                                    label={`${filterConfig.description} ${t("dashboard:filters.Final")}:`}
                                                                        value={convertIntUnixToFormateDate(selectedFilter.dateValues.finalDate)}
                                                                        onRemove={() => handleRemoveDateFilter(selectedFilter.name, false)}
                                                                    />
                                                                )}
                                                            </Stack>
                                                    </Col>
                                                );
                                            }

                                            // SENTIMENT filter
                                            if (isSentimentFilter(selectedFilter.name)) {
                                                const vals = selectedFilter.values ?? [];
                                                if (!vals.length) return null;
                                                return (
                                                    <Col xs="auto" key={selectedFilter.name}>
                                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                            <span>{t(filterConfig.description)}:</span>
                                                            <Stack direction="horizontal" gap={2}>
                                                                {vals.map((val) => (
                                                                    <SelectedFilterChip
                                                                        key={val}
                                                                        label=""
                                                                        value={val}
                                                                        onRemove={() => handleRemoveFilter(selectedFilter.name, val)}
                                                                    />
                                                                ))}
                                                            </Stack>
                                                        </div>
                                                    </Col>
                                                );
                                            }

                                            return (
                                                <Col xs="auto" key={selectedFilter.name}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                        <span>{t(filterConfig.description)}:</span>
                                                        <Stack direction="horizontal" gap={2}>
                                                            {selectedFilter.values?.map((value) => {
                                                                const options = filterConfig.values as KeyValuePair[] | undefined;
                                                                const option = options?.find((opt) => String(opt.key) === String(value));
                                                                const displayValue = getDisplayValue(option, value);
                                                                return (
                                                                    <SelectedFilterChip
                                                                        key={value}
                                                                        label=""
                                                                        value={t(String(displayValue))}
                                                                        onRemove={() =>
                                                                            handleRemoveFilter(selectedFilter.name, value)
                                                                        }
                                                                    />
                                                                );
                                                            })}
                                                        </Stack>
                                                    </div>
                                                </Col>
                                            )
                                        })
                                    }
                                    <Col className="ms-auto my-2">
                                        <Button
                                            variant="btn btn-primary btn-sm"
                                            onClick={handleClearAllFilters}
                                        >
                                            {t("common:removeFilters")}
                                        </Button>
                                    </Col>
                                </Row>
                            )}
                        </Container>
                    </Row>
                }
            </Container>
        </div>
    );
}
